// src/modules/agentic/MultiAgentLab.jsx
// Multi-Agent Lab — roles · orchestrator · debate · swarm · handoff · emergent behavior

import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = '#f87171';
import { _livePalette as P } from '../../context/paletteSync';

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=1,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);

// ─── AGENT DEFINITIONS ────────────────────────────────────────────────────────
const AGENT_ROLES = [
  {id:'orchestrator', name:'Orchestrator',  icon:'🎯', color:'#22d3ee', desc:'Coordinates all agents. Breaks goal into tasks, assigns to specialists, merges results.'},
  {id:'researcher',   name:'Researcher',    icon:'🔍', color:'#4ade80', desc:'Searches the web, reads documents, extracts information. Feeds raw data to other agents.'},
  {id:'coder',        name:'Coder',         icon:'⚡', color:'#a78bfa', desc:'Writes, tests, and debugs code. Executes in sandboxed Python/JS environment.'},
  {id:'critic',       name:'Critic',        icon:'🔬', color:'#fbbf24', desc:'Reviews outputs from other agents. Finds flaws, suggests improvements, rates quality.'},
  {id:'writer',       name:'Writer',        icon:'✍',  color:'#fb923c', desc:'Synthesizes research into polished text. Handles formatting, tone, and clarity.'},
  {id:'executor',     name:'Executor',      icon:'🚀', color:'#f87171', desc:'Deploys code, sends emails, calls APIs. The agent with real-world side effects.'},
];

// ─── ORCHESTRATOR → AGENT NETWORK SVG ────────────────────────────────────────
function NetworkSVG({ agents, messages, activeEdge }) {
  const W=680, H=280, cx=340, cy=140;
  const R=110;
  const toRad = d => d * Math.PI / 180;

  const positions = agents.map((a,i) => {
    const angle = (i/agents.length)*360 - 90;
    return { ...a, x: cx + R*Math.cos(toRad(angle)), y: cy + R*Math.sin(toRad(angle)) };
  });

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
      {/* Orchestrator center */}
      <circle cx={cx} cy={cy} r={38} fill="#22d3ee10" stroke="#22d3ee" strokeWidth={2}/>
      <text x={cx} y={cy-6} textAnchor="middle" style={{fontSize:'10px',fill:'#22d3ee',fontFamily:'Syne',fontWeight:800}}>ORCHESTRATOR</text>
      <text x={cx} y={cy+8} textAnchor="middle" style={{fontSize:'8px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>coordinates</text>

      {/* Edges */}
      {positions.map((a,i) => {
        const isActive = activeEdge === a.id;
        return (
          <g key={`e${i}`}>
            <line x1={cx} y1={cy} x2={a.x} y2={a.y}
              stroke={isActive ? a.color : '#1e293b'} strokeWidth={isActive ? 2 : 1}
              strokeDasharray={isActive ? 'none' : '4 3'} style={{transition:'stroke .3s'}}/>
            {isActive && (
              <circle r={5} fill={a.color}>
                <animateMotion dur="0.6s" repeatCount="1" path={`M${cx},${cy} L${a.x},${a.y}`}/>
              </circle>
            )}
          </g>
        );
      })}

      {/* Agent nodes */}
      {positions.map((a,i) => (
        <g key={a.id}>
          <circle cx={a.x} cy={a.y} r={28} fill={activeEdge===a.id?a.color+'22':'#0f172a'} stroke={a.color} strokeWidth={activeEdge===a.id?2.5:1.5} style={{transition:'all .3s'}}/>
          <text x={a.x} y={a.y-5} textAnchor="middle" style={{fontSize:'14px'}}>{a.icon}</text>
          <text x={a.x} y={a.y+10} textAnchor="middle" style={{fontSize:'7px',fill:a.color,fontFamily:'JetBrains Mono',fontWeight:700}}>{a.name}</text>
        </g>
      ))}

      {/* Message log */}
      {messages.slice(-3).map((m,i) => (
        <text key={i} x={W-10} y={H-30+i*12} textAnchor="end" style={{fontSize:'7px',fill:m.color||'#475569',fontFamily:'JetBrains Mono'}}>{m.text}</text>
      ))}
    </svg>
  );
}

// ─── DEBATE SIMULATION ────────────────────────────────────────────────────────
const DEBATE_ROUNDS = [
  {
    topic: 'Should AI models self-improve without human oversight?',
    rounds: [
      { agent:'Agent A (Pro)', color:'#4ade80', msg:'Self-improvement enables rapid capability gains. Humans can set initial values and constraints, then allow iterative refinement. Historical evidence shows that autonomy yields better optimization.' },
      { agent:'Agent B (Con)', color:'#f87171', msg:'Without oversight, capability gain outpaces alignment verification. We cannot validate values were preserved after each modification cycle. The risk profile scales superlinearly.' },
      { agent:'Agent A (Pro)', color:'#4ade80', msg:'Formal verification methods and constitutional constraints can bound the solution space. Oversight becomes a mathematical property, not a manual review process.' },
      { agent:'Agent B (Con)', color:'#f87171', msg:'Formal verification is incomplete for sufficiently complex systems. Goodhart\'s Law: any measure optimized becomes a bad measure. The verifier itself needs verification.' },
      { agent:'Mediator',      color:'#fbbf24', msg:'Synthesis: Staged autonomy with verifiable checkpoints. Self-improvement bounded within pre-approved capability domains, with human sign-off before each domain expansion.' },
    ]
  },
  {
    topic: 'Is GPT-4 architecture better than mixture-of-experts for coding tasks?',
    rounds: [
      { agent:'Agent A (Dense)', color:'#22d3ee', msg:'Dense transformers provide consistent, predictable behavior. All parameters active means richer cross-token interactions. GPT-4 benchmark results show superior reasoning depth.' },
      { agent:'Agent B (MoE)',   color:'#a78bfa', msg:'MoE routes tokens to specialist experts. Code tokens activate code-trained experts — this specialization outperforms uniform dense networks at equal parameter count.' },
      { agent:'Agent A (Dense)', color:'#22d3ee', msg:'Expert routing adds overhead and can misroute novel patterns. Dense networks generalize better to out-of-distribution code patterns like new frameworks.' },
      { agent:'Agent B (MoE)',   color:'#a78bfa', msg:'Mixtral-8x7B at 12.9B active params outperforms Llama-2 70B on HumanEval. Efficiency argument is decisive — same quality at 5× less compute.' },
      { agent:'Mediator',        color:'#fbbf24', msg:'Synthesis: MoE wins on efficiency. Dense wins on consistency. Hybrid: dense attention layers with MoE FFN layers — this is what GPT-4 likely uses internally.' },
    ]
  }
];

// ─── SWARM SIMULATION ─────────────────────────────────────────────────────────
const simSwarm = (n, iterations) => {
  // Simulate particle swarm finding a solution
  const pts = Array.from({length:n}, (_,i) => ({
    id: i,
    x: Math.random()*10-5,
    y: Math.random()*10-5,
    vx: (Math.random()-0.5)*0.5,
    vy: (Math.random()-0.5)*0.5,
    best: 99,
  }));
  const data = [];
  let gBest = 99;
  for(let it=0; it<iterations; it++){
    pts.forEach(p => {
      const score = p.x**2 + p.y**2; // minimize to origin
      if(score < p.best) p.best = score;
      if(score < gBest) gBest = score;
      p.vx = 0.7*p.vx + 0.15*Math.random()*(0-p.x) + 0.15*Math.random()*(0-p.x);
      p.vy = 0.7*p.vy + 0.15*Math.random()*(0-p.y) + 0.15*Math.random()*(0-p.y);
      p.x += p.vx; p.y += p.vy;
    });
    if(it % Math.floor(iterations/40) === 0)
      data.push({iter:it, gBest:+gBest.toFixed(4), avgDist:+(pts.reduce((s,p)=>s+Math.sqrt(p.x**2+p.y**2),0)/n).toFixed(3)});
  }
  return {data, final:pts};
};

// ─── HANDOFF FLOW ─────────────────────────────────────────────────────────────
const HANDOFF_STEPS = [
  {from:'User',        to:'Orchestrator', msg:'Build me a competitive analysis report on AI coding tools',  color:'#22d3ee'},
  {from:'Orchestrator',to:'Researcher',   msg:'Search: top AI coding tools 2024, GitHub Copilot, Cursor, Tabnine benchmarks', color:'#4ade80'},
  {from:'Researcher',  to:'Orchestrator', msg:'Found 12 sources. Key players: Copilot, Cursor, Codeium, Tabnine. Data attached.', color:'#4ade80'},
  {from:'Orchestrator',to:'Coder',        msg:'Write Python to parse benchmark CSV and compute rankings', color:'#a78bfa'},
  {from:'Coder',       to:'Orchestrator', msg:'Script done. Output: ranked table + charts generated. ✓', color:'#a78bfa'},
  {from:'Orchestrator',to:'Writer',       msg:'Synthesize research + data into 3-page executive report', color:'#fb923c'},
  {from:'Writer',      to:'Critic',       msg:'Draft v1 ready for review — please check accuracy + tone', color:'#fbbf24'},
  {from:'Critic',      to:'Writer',       msg:'3 factual errors found. Section 2 tone too casual. Revise.', color:'#fbbf24'},
  {from:'Writer',      to:'Orchestrator', msg:'Revised v2 — all critic notes addressed. Final draft ready.', color:'#fb923c'},
  {from:'Orchestrator',to:'User',         msg:'Report complete: 3 pages, 6 tools ranked, exec summary included.', color:'#22d3ee'},
];

const TABS = ['network','debate','swarm','handoff','frameworks'];

export default function MultiAgentLab() {
  const [tab, setTab] = useState('network');
  const [logs, setLogs] = useState([{ts:'00:00:00',msg:'Multi-Agent Lab — orchestrators, debate, swarms, and handoff protocols.',t:'info'}]);
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  // Network anim
  const [activeEdge, setActiveEdge] = useState(null);
  const [netMessages, setNetMessages] = useState([]);
  const [netRunning, setNetRunning] = useState(false);
  const netRef = useRef();

  // Debate
  const [debateTopic, setDebateTopic] = useState(0);
  const [debateStep, setDebateStep] = useState(0);
  const [debateRunning, setDebateRunning] = useState(false);
  const debRef = useRef();

  // Swarm
  const [swarmN, setSwarmN] = useState(10);
  const [swarmIter, setSwarmIter] = useState(80);
  const [swarmResult, setSwarmResult] = useState(null);

  // Handoff
  const [handoffStep, setHandoffStep] = useState(0);
  const [handoffRunning, setHandoffRunning] = useState(false);
  const hofRef = useRef();

  const runNetwork = () => {
    if(netRunning){ clearInterval(netRef.current); setNetRunning(false); setActiveEdge(null); return; }
    setNetRunning(true);
    const agents = AGENT_ROLES.filter(a=>a.id!=='orchestrator');
    let i=0;
    netRef.current = setInterval(()=>{
      const a = agents[i % agents.length];
      setActiveEdge(a.id);
      setNetMessages(m=>[...m.slice(-5),{text:`→ ${a.name}: task assigned`,color:a.color}]);
      i++;
    }, 900);
  };

  const runDebate = () => {
    setDebateStep(0); setDebateRunning(true);
    const rounds = DEBATE_ROUNDS[debateTopic].rounds;
    let i=0;
    debRef.current = setInterval(()=>{ i++; setDebateStep(i); if(i>=rounds.length){ clearInterval(debRef.current); setDebateRunning(false); log('Debate complete — synthesis reached','ok'); }}, 1000);
  };

  const runSwarm = () => {
    const r = simSwarm(swarmN, swarmIter);
    setSwarmResult(r);
    log(`Swarm: ${swarmN} agents, ${swarmIter} iterations → best=${r.data.at(-1)?.gBest}`,'ok');
  };

  const runHandoff = () => {
    setHandoffStep(0); setHandoffRunning(true);
    let i=0;
    hofRef.current = setInterval(()=>{ i++; setHandoffStep(i); if(i>=HANDOFF_STEPS.length){ clearInterval(hofRef.current); setHandoffRunning(false); log('Handoff chain complete','ok'); }}, 800);
  };

  useEffect(()=>()=>{clearInterval(netRef.current);clearInterval(debRef.current);clearInterval(hofRef.current);},[]);

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* ── NETWORK ── */}
      {tab==='network'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="ORCHESTRATOR → AGENT NETWORK — LIVE TASK DISPATCH" accent={AC}>
            <NetworkSVG agents={AGENT_ROLES.filter(a=>a.id!=='orchestrator')} messages={netMessages} activeEdge={activeEdge}/>
            <div style={{display:'flex',gap:'8px',marginTop:'10px'}}>
              <Btn onClick={runNetwork} color={netRunning?'#f87171':AC} style={{flex:1}}>{netRunning?'STOP':'▶ RUN DISPATCH'}</Btn>
              <Btn onClick={()=>{clearInterval(netRef.current);setNetRunning(false);setActiveEdge(null);setNetMessages([]);}} color="#64748b">RESET</Btn>
            </div>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px'}}>
            {AGENT_ROLES.filter(a=>a.id!=='orchestrator').map(a=>(
              <div key={a.id} style={{padding:'10px 12px',background:'var(--deep)',borderRadius:'4px',borderLeft:`3px solid ${a.color}`}}>
                <div style={{display:'flex',gap:'6px',alignItems:'center',marginBottom:'5px'}}>
                  <span style={{fontSize:'14px'}}>{a.icon}</span>
                  <span style={{fontSize:'10px',color:a.color,fontFamily:'JetBrains Mono',fontWeight:700}}>{a.name}</span>
                </div>
                <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6}}>{a.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── DEBATE ── */}
      {tab==='debate'&&(
        <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="TOPIC" accent="#a78bfa">
              {DEBATE_ROUNDS.map((d,i)=>(
                <button key={i} onClick={()=>{setDebateTopic(i);setDebateStep(0);clearInterval(debRef.current);setDebateRunning(false);}} style={{display:'block',width:'100%',padding:'8px',background:debateTopic===i?'#a78bfa18':'var(--deep)',border:`1px solid ${debateTopic===i?'#a78bfa':'var(--b0)'}`,color:debateTopic===i?'#a78bfa':'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'3px',marginBottom:'4px',fontFamily:'JetBrains Mono',textAlign:'left',lineHeight:1.5}}>
                  TOPIC {i+1}<br/><span style={{fontWeight:400,fontSize:'8px',color:'var(--t3)'}}>{d.topic.slice(0,52)}…</span>
                </button>
              ))}
              <Btn onClick={runDebate} disabled={debateRunning} color="#a78bfa" style={{width:'100%',marginTop:'4px'}}>{debateRunning?'DEBATING…':'▶ START DEBATE'}</Btn>
            </Panel>
            <Panel title="WHY DEBATE?" accent="#fbbf24">
              <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.8}}>
                Multiple agents with opposing views catch errors single agents miss. "Society of Mind" approach: disagreement → better synthesis.<br/><br/>
                <span style={{color:'#4ade80'}}>+15-30%</span> accuracy on ambiguous tasks vs single-agent.
              </div>
            </Panel>
          </div>
          <Panel title={`AGENT DEBATE — "${DEBATE_ROUNDS[debateTopic].topic}"`} accent="#a78bfa">
            <div style={{display:'flex',flexDirection:'column',gap:'5px'}}>
              {DEBATE_ROUNDS[debateTopic].rounds.slice(0,debateStep).map((r,i)=>(
                <div key={i} style={{padding:'10px 14px',background:'var(--deep)',borderRadius:'3px',borderLeft:`3px solid ${r.color}`,animation:'fadeIn .4s'}}>
                  <div style={{fontSize:'9px',color:r.color,fontFamily:'JetBrains Mono',fontWeight:700,marginBottom:'5px'}}>{r.agent}</div>
                  <div style={{fontSize:'10px',color:'var(--t2)',lineHeight:1.6}}>{r.msg}</div>
                </div>
              ))}
              {!debateStep&&<div style={{height:160,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Select topic and start debate</div>}
            </div>
            {debateStep>0&&<div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px',marginTop:'10px'}}>
              <Metric label="Rounds" value={debateStep} color="#a78bfa"/>
              <Metric label="Total rounds" value={DEBATE_ROUNDS[debateTopic].rounds.length} color="#fbbf24"/>
              <Metric label="Status" value={debateStep>=DEBATE_ROUNDS[debateTopic].rounds.length?'RESOLVED':'…'} color="#4ade80"/>
            </div>}
          </Panel>
        </div>
      )}

      {/* ── SWARM ── */}
      {tab==='swarm'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
            <Panel title="SWARM CONFIG" accent="#4ade80">
              <Sl label="Agents" value={swarmN} min={3} max={30} step={1} onChange={setSwarmN} color="#4ade80"/>
              <Sl label="Iterations" value={swarmIter} min={20} max={200} step={10} onChange={setSwarmIter} color="#fbbf24"/>
              <Btn onClick={runSwarm} color="#4ade80" style={{width:'100%',marginTop:'6px'}}>RUN SWARM →</Btn>
              {swarmResult&&<div style={{marginTop:'10px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px'}}>
                <Metric label="Best score" value={swarmResult.data.at(-1)?.gBest} color="#4ade80"/>
                <Metric label="Avg dist" value={swarmResult.data.at(-1)?.avgDist} color="#fbbf24"/>
              </div>}
            </Panel>
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              {swarmResult?(
                <Panel title="SWARM CONVERGENCE — GLOBAL BEST vs AVG DISTANCE" accent="#4ade80">
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={swarmResult.data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="iter" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'iteration',fill:'var(--t3)',fontSize:8,position:'insideBottom',offset:-2}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <Tooltip/><Legend/>
                      <Line type="monotone" dataKey="gBest"   stroke="#4ade80" strokeWidth={2} dot={false} name="Global best"/>
                      <Line type="monotone" dataKey="avgDist" stroke="#fbbf24" strokeWidth={2} dot={false} name="Avg distance"/>
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
              ):<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:200,color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Configure and run swarm</div>}
            </div>
          </div>
          <Panel title="SWARM INTELLIGENCE PATTERNS IN MULTI-AGENT AI" accent="#fbbf24">
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px'}}>
              {[
                {name:'Ant Colony (ACO)',desc:'Agents leave pheromone trails on good solutions. Collective reinforcement finds optimal paths through solution space.',use:'Routing, scheduling, combinatorial optimization',color:'#4ade80'},
                {name:'Particle Swarm (PSO)',desc:'Each agent has position + velocity. Attracted to personal best and global best. Emergent convergence without central control.',use:'Hyperparameter search, continuous optimization',color:'#22d3ee'},
                {name:'LLM Swarm',desc:'Many LLM instances run in parallel on sub-problems. Results aggregated via voting, merging, or critic review. Emergent reasoning.',use:'Code generation, creative writing, fact-checking',color:'#a78bfa'},
              ].map(({name,desc,use,color})=>(
                <div key={name} style={{padding:'12px',background:'var(--deep)',borderRadius:'4px',borderTop:`2px solid ${color}`}}>
                  <div style={{fontSize:'10px',color,fontWeight:700,marginBottom:'6px'}}>{name}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6,marginBottom:'5px'}}>{desc}</div>
                  <div style={{fontSize:'8px',color:color}}>Use: {use}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── HANDOFF ── */}
      {tab==='handoff'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="AGENT HANDOFF CHAIN — COMPETITIVE ANALYSIS TASK" accent="#fb923c">
            <div style={{display:'flex',flexDirection:'column',gap:'4px',marginBottom:'10px'}}>
              {HANDOFF_STEPS.slice(0,handoffStep).map((s,i)=>{
                const fromAgent = AGENT_ROLES.find(a=>a.name===s.from||s.from==='User');
                const toAgent   = AGENT_ROLES.find(a=>a.name===s.to||s.to==='User');
                return (
                  <div key={i} style={{display:'grid',gridTemplateColumns:'110px 20px 110px 1fr',gap:'8px',alignItems:'center',padding:'8px 10px',background:'var(--deep)',borderRadius:'3px',animation:'fadeIn .3s',borderLeft:`2px solid ${s.color}`}}>
                    <span style={{fontSize:'9px',color:s.color,fontFamily:'JetBrains Mono',fontWeight:700,textAlign:'right'}}>{s.from}</span>
                    <span style={{color:'#475569',textAlign:'center',fontSize:'10px'}}>→</span>
                    <span style={{fontSize:'9px',color:s.color,fontFamily:'JetBrains Mono',fontWeight:700}}>{s.to}</span>
                    <span style={{fontSize:'9px',color:'var(--t3)'}}>{s.msg}</span>
                  </div>
                );
              })}
              {!handoffStep&&<div style={{height:100,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Run the handoff chain</div>}
            </div>
            <div style={{display:'flex',gap:'8px'}}>
              <Btn onClick={runHandoff} disabled={handoffRunning} color="#fb923c" style={{flex:1}}>{handoffRunning?'RUNNING…':'▶ RUN HANDOFF CHAIN'}</Btn>
              <Btn onClick={()=>{clearInterval(hofRef.current);setHandoffRunning(false);setHandoffStep(0);}} color="#64748b">RESET</Btn>
            </div>
            {handoffStep>0&&<div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginTop:'8px'}}>
              <Metric label="Steps done" value={handoffStep} color="#fb923c"/>
              <Metric label="Agents involved" value={new Set(HANDOFF_STEPS.slice(0,handoffStep).flatMap(s=>[s.from,s.to])).size} color="#22d3ee"/>
              <Metric label="Total steps" value={HANDOFF_STEPS.length} color="#64748b"/>
              <Metric label="Complete" value={handoffStep>=HANDOFF_STEPS.length?'✓':'…'} color="#4ade80"/>
            </div>}
          </Panel>
          <Panel title="HANDOFF PROTOCOLS" accent="#22d3ee">
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
              {[
                {name:'Context passing',desc:'Full conversation + task state serialized and passed to next agent. Safe but expensive — large context.',color:'#22d3ee'},
                {name:'Structured handoff',desc:'Defined schema: {task, results, next_steps, constraints}. Clean boundaries, less token bloat.',color:'#4ade80'},
                {name:'Shared memory',desc:'All agents read/write to shared vector DB or key-value store. Decoupled — agents don\'t need to know about each other.',color:'#a78bfa'},
              ].map(({name,desc,color})=>(
                <div key={name} style={{padding:'10px',background:'var(--deep)',borderRadius:'3px',borderTop:`2px solid ${color}`}}>
                  <div style={{fontSize:'10px',color,fontWeight:700,marginBottom:'5px'}}>{name}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6}}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── FRAMEWORKS ── */}
      {tab==='frameworks'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
            {[
              {name:'AutoGen',org:'Microsoft',color:'#22d3ee',model:'Conversational agents. Each agent is an LLM with a persona. They message each other to solve tasks.',pro:'Flexible, easy setup, good for coding tasks',con:'Hard to control flow, can loop',best:'Code generation, data analysis'},
              {name:'CrewAI',org:'CrewAI Inc',color:'#4ade80',model:'Role-based crews. Define Agent (role+goal+backstory) + Task + Crew. Sequential or parallel execution.',pro:'Clean abstraction, production-ready',con:'Less flexible than raw AutoGen',best:'Structured business workflows'},
              {name:'LangGraph',org:'LangChain',color:'#a78bfa',model:'Graph-based agent flows. Nodes = agents/tools. Edges = conditional routing. Full state machine control.',pro:'Maximum control, complex flows',con:'More boilerplate, learning curve',best:'Production pipelines, complex logic'},
              {name:'MetaGPT',org:'MetaGPT',color:'#fbbf24',model:'Simulates a software company: PM, Architect, Engineer, QA roles. Produces full codebases from a prompt.',pro:'Impressive end-to-end code output',con:'Slow, expensive, opinionated',best:'Full software project generation'},
              {name:'AutoGPT',org:'Significant Gravitas',color:'#fb923c',model:'Self-prompting loop: goal → plan → execute → reflect → repeat. Fully autonomous long-horizon tasks.',pro:'Truly autonomous, self-directing',con:'Expensive, prone to loops, unreliable',best:'Research tasks, exploration'},
              {name:'Claude Agents',org:'Anthropic',color:'#f87171',model:'Tool use + computer use. Multi-turn with persistent memory. Constitutional AI safety baked in.',pro:'Safe, reliable, multimodal',con:'Requires Anthropic API, rate limits',best:'Safe production deployments'},
            ].map(f=>(
              <Panel key={f.name} title={`${f.name}`} accent={f.color}>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px'}}>by {f.org}</div>
                <div style={{fontSize:'9px',color:'var(--t2)',lineHeight:1.6,marginBottom:'6px'}}>{f.model}</div>
                <div style={{fontSize:'9px',marginBottom:'2px'}}><span style={{color:'#4ade80'}}>✓ </span><span style={{color:'var(--t3)'}}>{f.pro}</span></div>
                <div style={{fontSize:'9px',marginBottom:'2px'}}><span style={{color:'#f87171'}}>✗ </span><span style={{color:'var(--t3)'}}>{f.con}</span></div>
                <div style={{fontSize:'9px',marginTop:'4px',padding:'4px 6px',background:'var(--deep)',borderRadius:'2px',borderLeft:`2px solid ${f.color}`}}><span style={{color:'var(--t3)'}}>Best: </span><span style={{color:f.color}}>{f.best}</span></div>
              </Panel>
            ))}
          </div>
          <LogPanel logs={logs} height={70}/>
        </div>
      )}
    </div>
  );
}
